import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Card, Checkbox, InputAdornment, Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { SearchRounded as SearchIcon } from '@mui/icons-material';
import PayrollService, { PayRule, Person, describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import PayrollPanel from './PayrollPanel';
import RuleEditor from './RuleEditor';
import BulkRuleEditor from './BulkRuleEditor';
import { Avatar } from './payrollUI';

// Everyone in the company and how each is paid. Pick a person on the
// left, edit their rules on the right. Set once, changed when the deal
// changes; this is not a screen anybody lives in. Tick several people and
// the right side changes all of them at once.

// The list row names the kind of deal, not the whole formula: the full
// description is in the editor, and in a 300px column it only got cut off.
const money = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
function shortFormula(rules: PayRule[]): string {
  const on = rules.filter((r) => r.enabled !== false && r.amount != null);
  const first = (kind: PayRule['kind']) => on.find((r) => r.kind === kind);
  const day = first('day_rate');
  const stop = first('per_stop');
  if (day && stop) return 'Mixed';
  if (day) return `${money(day.amount!)} a day`;
  if (stop) return `${money(stop.amount!)} a stop`;
  const hour = first('hourly');
  if (hour) return `${money(hour.amount!)} an hour`;
  const salary = first('weekly_salary');
  if (salary) return `${money(salary.amount!)} a week`;
  return 'Custom';
}

const DriverPay: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));

  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Ticked people, for one change to all of them.
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPeople(await PayrollService.people());
    } catch (err) {
      setError(describeError(err, 'Could not load the people.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => people.find((p) => p.accountId === accountId) || null, [people, accountId]);
  usePageBreadcrumb(selected ? [{ label: 'Driver pay', to: '/payroll/people' }, { label: selected.name }] : []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? people.filter((p) => [p.name, p.fedexId, p.description].some((s) => s?.toLowerCase().includes(q))) : people;
    // The ones still to set up float to the top: that is the work.
    return [...list].sort((a, b) => Number(b.hasRules) - Number(a.hasRules) === 0 ? a.name.localeCompare(b.name) : Number(a.hasRules) - Number(b.hasRules));
  }, [people, search]);

  // Side by side, an empty right half makes the page look broken: open
  // the person at the top of the list until somebody else is picked.
  const first = visible[0]?.accountId;
  useEffect(() => {
    if (wide && !accountId && first) navigate(`/payroll/people/${first}`, { replace: true });
  }, [wide, accountId, first, navigate]);

  const todo = people.filter((p) => !p.hasRules).length;
  const bulk = checked.size > 0;
  const ticked = people.filter((p) => checked.has(p.accountId));
  const allVisibleTicked = visible.length > 0 && visible.every((p) => checked.has(p.accountId));
  const tick = (id: string) => setChecked((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tickAll = () => setChecked((c) => {
    const n = new Set(c);
    if (allVisibleTicked) visible.forEach((p) => n.delete(p.accountId)); else visible.forEach((p) => n.add(p.accountId));
    return n;
  });
  const showList = wide || (!selected && !bulk);
  const showEditor = !bulk && selected && (wide || !!accountId);

  return (
    <PageBody>
      <PayrollPanel onUploaded={() => load()} />
      <PageHeader
        title="Driver pay"
        subtitle={todo ? `${todo} ${todo === 1 ? 'person' : 'people'} without pay rules` : 'How each person is paid'}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <LoadingState /> : (
        <Stack direction="row" gap={2.5} alignItems="flex-start">
          {showList && (
            <Card sx={{ width: wide ? 300 : '100%', flexShrink: 0, p: 1.25, position: wide ? 'sticky' : 'static', top: 16 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Find a person"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 0.5 }}
              />
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <Checkbox
                  size="small"
                  sx={{ p: 0.75 }}
                  checked={allVisibleTicked}
                  indeterminate={!allVisibleTicked && visible.some((p) => checked.has(p.accountId))}
                  onChange={tickAll}
                  disabled={!visible.length}
                  inputProps={{ 'aria-label': search ? 'Select everyone shown' : 'Select everyone' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {checked.size ? `${checked.size} selected` : 'Select to change several at once'}
                </Typography>
              </Stack>
              {visible.length === 0 && <EmptyState title="Nobody matches" compact />}
              <Stack gap={0.25} sx={{ maxHeight: wide ? 'calc(100vh - 260px)' : undefined, overflowY: 'auto' }}>
                {visible.map((p) => {
                  const active = p.accountId === accountId;
                  return (
                    <Stack key={p.accountId} direction="row" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={checked.has(p.accountId)}
                      onChange={() => tick(p.accountId)}
                      inputProps={{ 'aria-label': `Select ${p.name}` }}
                      sx={{ p: 0.75 }}
                    />
                    <Box
                      component="button"
                      type="button"
                      onClick={() => navigate(`/payroll/people/${p.accountId}`)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, textAlign: 'left',
                        px: 1, py: 1, borderRadius: 2, border: 0, cursor: 'pointer', font: 'inherit',
                        bgcolor: active ? 'primary.light' : 'transparent',
                        boxShadow: !p.hasRules ? 'inset 3px 0 0 var(--topo-status-warn)' : 'none',
                        '&:hover': { bgcolor: active ? 'primary.light' : 'grey.100' },
                      }}
                    >
                      <Avatar name={p.name} muted={!p.hasRules} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: active ? 'primary.dark' : 'text.primary', overflowWrap: 'anywhere' }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          <Box component="span" sx={p.hasRules ? undefined : { color: 'warning.dark', fontWeight: 600 }}>
                            {p.hasRules ? shortFormula(p.rules) : p.profileId ? 'Amounts missing' : 'No rules yet'}
                          </Box>
                          {p.status === 'former' && ' · former'}
                        </Typography>
                      </Box>
                    </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Card>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {bulk ? (
              <BulkRuleEditor
                selected={ticked}
                onClear={() => setChecked(new Set())}
                // Quietly: a full reload would swap the page for a spinner and lose the result.
                onApplied={() => { PayrollService.people().then(setPeople).catch(() => undefined); }}
              />
            ) : showEditor && selected ? (
              <RuleEditor
                person={selected}
                people={people}
                onSaved={(saved) => setPeople((ps) => ps.map((p) => (p.accountId === saved.accountId ? { ...p, ...saved } : p)))}
              />
            ) : wide ? (
              <EmptyState title="Pick a person" />
            ) : null}
          </Box>
        </Stack>
      )}
    </PageBody>
  );
};

export default DriverPay;
